/*    
<summary>
  This functional component "AddManagTreatmentementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import Popover from "@mui/material/Popover";
import { RichTreeView } from "@mui/x-tree-view";
import dayjs from "dayjs";
import { Field, Form, Formik, useFormikContext, type FieldProps } from "formik";
import moment from "moment";
import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { DATE_FORMAT, integerRegex } from "../../../../constants/constants";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { useStore } from "../../../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";
import { getUserTypeFromLocalStorage } from "../../../../helpers/localstorage-helper";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import type { IOption } from "../../../../models/ICommon";
import AutoCalculateTax from "../../../../shared-components/auto-tax-calculation/useAutoCalculateTax";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikDateFieldMUI from "../../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputRegexCheckFormattedNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputRegexCheckFormattedNumber";
import FormikFormOnChangeSelect from "../../../../shared-components/FormikFormHandlers/FormikFormOnChangeSelect";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import {
  AddTreatmentValidationSchema,
  EditTreatmentValidationSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import Label from "../../../../shared-components/label/label";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import { formatMessage } from "../../../../translations/format-message";
import "./add-edit-treatment-form.css";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  getTreatmentManagementLoading: boolean;
  initialValues: any;
  businessTypeOptions: IOption[];
  categoryOptions: IOption[];
  timeOptions: IOption[];
  taxOptions: IOption[];
  registrationTypeOptions: IOption[];
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
  handleDeleteSubmitHandler: () => void;
  deleteActionLoading: boolean;
  viewOnly: boolean;
}

const AddTreatmentManagementForm = (props: IProps) => {
  const {
    showDeletePopup,
    deletePopupToggleHandler,
    handleDeleteSubmitHandler,
    deleteActionLoading,
    viewOnly,
  } = props;
  const { categoryManagementStore } = useStore();
  const { GetCategoryManagementListFilterService } = categoryManagementStore;
  const [selectedItems, setSelectedItems] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [itemDetail, setItemDetail] = useState(
    formatMessage("choose_category")
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBusinessType, setselectedBusinessType] = useState(-1);

  const isValidDate = (date: any) => {
    return dayjs(date).isValid();
  };

  useEffect(() => {
    if (props.id > 0) {
      let path = findObjByKeyWithPath(
        props.categoryOptions,
        props.initialValues?.CategoryId
      )?.path;
      let obj = findObjByKeyWithPath(
        props.categoryOptions,
        props.initialValues?.CategoryId
      )?.obj;
      setSelectedCategory(obj);
      setSelectedItems(obj?.id);
      setItemDetail(path ? path : formatMessage("choose_category"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.categoryOptions, props.initialValues]);

  const formik = useFormikContext();

  useEffect(() => {
    const businessTypeId =
      selectedBusinessType > -1
        ? selectedBusinessType
        : props.initialValues?.BusinessTypeId;

    if (businessTypeId > 0) {
      GetCategoryManagementListFilterService(businessTypeId, 1);
    }
    setSelectedItems("");
    setSelectedCategory(null);
    setItemDetail(formatMessage("choose_category"));

    if (formik) {
      formik.setFieldValue("CategoryPath", "");
      formik.setFieldTouched("CategoryPath", true);
    }
  }, [selectedBusinessType, props.initialValues]);

  const handleDelete = () => {
    deletePopupToggleHandler();
  };

  const handleDeleteModalClosed = () => {
    deletePopupToggleHandler();
  };

  const findObjByKeyWithPath = (
    data: any[],
    key: number
  ): { obj: any; path: string } | null => {
    const search = (
      data: any[],
      key: number,
      path: string[]
    ): { obj: any; path: string } | null => {
      for (const obj of data) {
        const currentPath = [...path, obj.title];

        if (obj.key === key) {
          return { obj, path: currentPath.join(" > ") };
        }

        if (obj.children && obj.children.length > 0) {
          const found = search(obj.children, key, currentPath);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    // Start the search with an empty path
    return search(data, key, []);
  };

  const isOpen = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const formatDate = (date: string | Date) => dayjs(date).format(DATE_FORMAT);

  return (
    <>
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={handleDeleteSubmitHandler}
          modalClosed={handleDeleteModalClosed}
          isLoading={deleteActionLoading}
          title={formatMessage("delete_confirmation")}
          message={formatMessage(
            "are_you_sure_you_want_to_delete_this_treatment"
          )}
        />
      )}
      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="treatment_registration" />
            ) : (
              <FormattedMessage id="treatment_details" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.getTreatmentManagementLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            ...props.initialValues,
          }}
          enableReinitialize
          validationSchema={
            props.id === -1
              ? AddTreatmentValidationSchema(selectedCategory)
              : EditTreatmentValidationSchema(selectedCategory)
          }
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            let obj = {
              RegistrationType: values.RegistrationType,
              BusinessTypeId: values.BusinessTypeId,
              CategoryId:
                selectedCategory !== null ? Number(selectedItems) : -1,
              TreatmentName: values.TreatmentName,
              FeeIncludingTax: toHalfWidth(values.FeeIncludingTax),
              TaxId: values.TaxId,
              TaxAmount: values.TaxAmount || 0,
              TaxValue:
                props.taxOptions.find(
                  (item: IOption) => item.id == values.TaxId
                )?.value ?? 0,
              Status: values.Status,
              StartDate: values.StartDate
                ? new Date(values.StartDate?.$d).toString() !== "Invalid Date"
                  ? moment(values.StartDate.$d).format(DATE_FORMAT)
                  : values.StartDate
                : null,
              EndDate: values.EndDate
                ? new Date(values.EndDate?.$d).toString() !== "Invalid Date"
                  ? moment(values.EndDate.$d).format(DATE_FORMAT)
                  : values.EndDate
                : null,
              Note: values?.Note,
              IsReservationSite: values.IsReservationSite,
              OptionMenu: Number(values.OptionMenu),
              Time: values.Time,
              TimeValue:
                props.timeOptions.find(
                  (item: IOption) => item.id == values.Time
                )?.value ?? 0,
              FeeExcludingTax: values.FeeExcludingTax || 0,
              Description: values.Description,
              IsCategoryChanged: false
            };
            
            if(obj.CategoryId !== props.initialValues?.CategoryId)
              obj.IsCategoryChanged = true;
            props.submitHandler(obj);
          }}
        >
          {({ setFieldValue, setFieldTouched, values }) => {
            const handleClick = async (
              event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
              fieldName: string
            ) => {
              const field = fieldName;

              if (field === "FeeExcludingTaxIncreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) + 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) - 1); // Uncheck the other checkbox
              } else if (field === "FeeExcludingTaxDecreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) - 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) + 1); // Uncheck the other checkbox
              }
              else if (field === "TaxAmountIncreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) - 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) + 1); // Uncheck the other checkbox
              } else if (field === "TaxAmountDecreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) + 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) - 1); // Uncheck the other checkbox
              }
            };
            return (
              <Form className="user-form">
                <AutoCalculateTax
                  id={props.id}
                  taxOptions={props.taxOptions}
                  feeExcludingTaxField="FeeExcludingTax"
                  taxIdField="TaxId"
                  taxAmountField="TaxAmount"
                  feeIncludingTaxField="FeeIncludingTax"
                />
                {props.getTreatmentManagementLoading && (
                  <FormLoader loading={props.getTreatmentManagementLoading} />
                )}
                <Modal.Body>
                  <div className="form-group mb-3 row">
                    <div className={`
                      ${getUserTypeFromLocalStorage() !==
                      userTypeEnum[userTypeEnum.HeadQuarter] ? "col-sm-6" : "col-sm-12"}
                      `}>
                      <Label
                        className="label-style"
                        required={false}
                        label="registration_type"
                      />
                      {props.id > 0 ? (
                        <Field
                          name="RegistrationTypeName"
                          as={FormikFormInput}
                          disabled={true}
                          className="form-control"
                          type="text"
                          validcharacter={["+", "(", ")", "-", "_", "&"]}
                        />
                      ) : (
                        <Field
                          name="RegistrationType"
                          as={FormikFormSelect}
                          options={props.registrationTypeOptions}
                          disabled={true}
                        />
                      )}
                    </div>

                    {getUserTypeFromLocalStorage() !==
                      userTypeEnum[userTypeEnum.HeadQuarter] && (
                      <div className="col-sm-6">
                        <Label
                          className="label-style"
                          required={true}
                          label="business_type"
                        />
                        <Field
                          name="BusinessTypeId"
                          as={FormikFormOnChangeSelect}
                          setState={setselectedBusinessType}
                          options={props.businessTypeOptions}
                          disabled={
                            getUserTypeFromLocalStorage() ===
                            userTypeEnum[userTypeEnum.HeadQuarter]
                              ? true
                              : viewOnly
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Row 2: Category, Reservation Site */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="category"
                      />
                      {/* <Field
                        name="CategoryId"
                        className="form-control"
                        as={FormikFormSelect}
                        options={props.categoryOptions}
                      /> */}
                      <Field
                        type="text"
                        className="form-control blinkCursor form-select"
                        name="CategoryPath"
                        onClick={handleOpen}
                        value={itemDetail}
                        as={FormikFormInput}
                        onBlur={() => {
                          if (!values.CategoryPath) {
                            setFieldTouched("CategoryPath", true);
                          }
                        }}
                        disabled={viewOnly}
                      />

                      <Popover
                        open={isOpen}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        disableAutoFocus
                      >
                        {props.categoryOptions?.length > 0 ? (
                          <RichTreeView
                            checkboxSelection
                            selectedItems={selectedItems}
                            onSelectedItemsChange={(
                              e: any,
                              newSelectedItems: any
                            ) => {
                              if (newSelectedItems) {
                                const selectedCategoryObj =
                                  findObjByKeyWithPath(
                                    props.categoryOptions,
                                    Number(newSelectedItems)
                                  )?.obj;

                                const categoryPath = findObjByKeyWithPath(
                                  props.categoryOptions,
                                  Number(newSelectedItems)
                                )?.path!;

                                setSelectedItems(newSelectedItems);
                                setSelectedCategory(selectedCategoryObj);
                                setItemDetail(categoryPath);

                                setFieldValue("CategoryPath", categoryPath);
                                setFieldTouched("CategoryPath", false, false);
                              } else {
                                setSelectedItems("");
                                setSelectedCategory(null);
                                setItemDetail(formatMessage("choose_category"));
                                setFieldValue("CategoryPath", "");
                                setFieldTouched("CategoryPath", true);
                              }
                              handleClose();
                            }}
                            items={props.categoryOptions}
                          />
                        ) : (
                          formatMessage("no_category_added")
                        )}
                      </Popover>
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="reservation_site"
                      />
                      <Field name="IsReservationSite">
                        {({ field, form }: FieldProps) => (
                          <div className="row">
                            <div className="col-sm-6">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="use"
                                name={field.name}
                                checked={field.value === true}
                                onChange={() =>
                                  form.setFieldValue(field.name, true)
                                }
                                disabled={viewOnly}
                              />
                              <label className="form-check-label" htmlFor="use">
                                <FormattedMessage id="use" />
                              </label>
                            </div>
                            <div className="col-sm-6">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="not_use"
                                name={field.name}
                                checked={field.value === false}
                                onChange={() =>
                                  form.setFieldValue(field.name, false)
                                }
                                disabled={viewOnly}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="not_use"
                              >
                                <FormattedMessage id="not_use" />
                              </label>
                            </div>
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>

                  {/* Row 3: Treatment Name, Time */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="treatment_name"
                      />
                      <Field
                        name="TreatmentName"
                        className="form-control"
                        type="text"
                        placeholder="enter_treatment_name"
                        // validcharacter={["+", "(", ")", "-", "_", "&"]}
                        // validcharacter={[
                        //   "+",
                        //   "-",
                        //   "[",
                        //   "]",
                        //   "|",
                        //   "*",
                        //   "&",
                        //   "/",
                        // ]}
                        validcharacter={[
                          "・",
                          "／",
                          "－",
                          "～",
                          "＋",
                          "（",
                          "）",
                          "［",
                          "］",
                          "※",
                          "：",
                          "★",
                          "☆",
                          "◎",
                          "○",
                          "●",
                          "◆",
                          "◇",
                          "■",
                          "□",
                          "→",
                          "⇒",
                          "♪",
                          "(",
                          ")",
                          "【",
                          "】",
                          "￥",
                          '"',
                          "＜",
                          "＞",
                          "！",
                          "？",
                          "↓",
                          "↑",
                          "←",
                          "．",
                          '"',
                          "゛",
                          "'",
                          "'",
                          '"',
                          '"',
                          "、",
                          "。",
                          "《",
                          "》",
                          "+",
                          "-",
                          "[",
                          "]",
                          "|",
                          "*",
                          "&",
                          "/",
                        ]}
                        as={FormikFormInput}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="time"
                      />
                      <Field
                        name="Time"
                        className="form-control"
                        as={FormikFormSelect}
                        options={props.timeOptions}
                        disabled={viewOnly}
                      />
                    </div>
                  </div>

                  {/* Row 4: Fee, Tax Rate */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="fee(including_tax)"
                      />
                      <Field
                        name="FeeIncludingTax"
                        className="form-control"
                        type="text"
                        placeholder="enter_fee"
                        regex={integerRegex}
                        as={FormikFormInputRegexCheckFormattedNumber}
                        disabled={viewOnly}
                      />
                    </div>

                    {/* Tax Dropdown */}
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="tax"
                      />
                      <Field
                        name="TaxId"
                        as={FormikFormSelect}
                        options={props.taxOptions}
                        disabled={viewOnly}
                      />
                    </div>
                  </div>

                  {/* Auto-Filled Fields */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label className="label-style" label="tax_amount" />
                      <div className="input-group">
                        <Field
                          name="TaxAmount"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                        <button
                        name="TaxAmountDecreaseBtn"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "TaxAmountDecreaseBtn")
                        }
                        className="btn btn-dark"
                        disabled={(Math.round(parseFloat(values.FeeIncludingTax) - (parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100)))) - 1 === values.TaxAmount) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      <button
                        name="TaxAmountIncreaseBtn"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "TaxAmountIncreaseBtn")
                        }
                        className="btn btn-dark"
                        disabled={(Math.round(parseFloat(values.FeeIncludingTax) - parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) + 1 === values.TaxAmount) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                    </div>
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        label="fee(excluding_tax)"
                      />
                      <div className="input-group">
                        <Field
                          name="FeeExcludingTax"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                        <button
                        name="FeeExcludingTaxDecreaseBtn"
                        className="btn btn-dark"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "FeeExcludingTaxDecreaseBtn")
                        }
                        disabled={(Math.round(parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) - 1 === values.FeeExcludingTax) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      <button
                        name="FeeExcludingTaxIncreaseBtn"
                        className="btn btn-dark"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "FeeExcludingTaxIncreaseBtn")
                        }
                        disabled={(Math.round(parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) + 1 === values.FeeExcludingTax)  || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                    </div>
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className="form-group mb-0">
                    <Label className="label-style" label="Description" />
                    <Field
                      name="Description"
                      as={FormikFormTextArea}
                      className="form-control"
                      placeholder="enter_description"
                      disabled={viewOnly}
                      validcharacter={[
                        "・",
                        "／",
                        "－",
                        "～",
                        "＋",
                        "（",
                        "）",
                        "［",
                        "］",
                        "※",
                        "：",
                        "★",
                        "☆",
                        "◎",
                        "○",
                        "●",
                        "◆",
                        "◇",
                        "■",
                        "□",
                        "→",
                        "⇒",
                        "♪",
                        "(",
                        ")",
                        "【",
                        "】",
                        "￥",
                        '"',
                        "＜",
                        "＞",
                        "！",
                        "？",
                        "↓",
                        "↑",
                        "←",
                        "．",
                        '"',
                        "゛",
                        "'",
                        "'",
                        '"',
                        '"',
                        "、",
                        "。",
                        "《",
                        "》",
                        "+",
                        "-",
                        "[",
                        "]",
                        "|",
                        "*",
                        "&",
                        "/",
                        ".",
                        "!",
                        "@",
                        "#",
                        "$",
                        "%",
                        "^",
                        "_",
                        "`",
                        "{",
                        "}",
                        "?",
                        ";",
                        ":",
                        ",",
                        "=",
                        "~",
                        "<",
                        ">",
                      ]}
                    />
                    <p className="small text-muted text-end mt-1">{`${
                      values.Description ? values.Description.length : 0
                    }/300`}</p>
                  </div>

                  {/* Row 5: Start Date, End Date */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="start_date"
                      />
                      <Field
                        name="StartDate"
                        as={FormikDateFieldMUI}
                        // min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="end_date"
                      />
                      <Field
                        name="EndDate"
                        as={FormikDateFieldMUI}
                        // min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        disabled={viewOnly}
                      />
                    </div>
                  </div>

                  {/* Row 6: Status, Counseling Notes */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="status"
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="0"
                            id="valid"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="valid">
                            <FormattedMessage id="valid" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="1"
                            id="invalid"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="invalid">
                            <FormattedMessage id="invalid" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="option_menu"
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="OptionMenu"
                            type="radio"
                            value="0"
                            id="mandatory"
                            disabled={viewOnly}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="mandatory"
                          >
                            <FormattedMessage id="mandatory" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="OptionMenu"
                            type="radio"
                            value="1"
                            id="not_mandatory"
                            disabled={viewOnly}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="not_mandatory"
                          >
                            <FormattedMessage id="not_mandatory" />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-3 row">
                    <div className="col-sm-12">
                      <Label
                        className="label-style"
                        required={false}
                        label="remarks"
                      />
                      <Field
                        name="Note"
                        id="CounsellingNote"
                        as={FormikFormTextArea}
                        className="form-control"
                        placeholder="enter_remarks"
                        type="text"
                        disabled={viewOnly}
                      />
                      <p className="small text-muted text-end mt-1">{`${
                        values.Note ? values.Note.length : 0
                      }/300`}</p>
                    </div>
                  </div>
                </Modal.Body>

                {/* Footer Buttons */}
                <div className="modal-footer center-content">
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={props.onModalClosed}
                    disabled={props.getTreatmentManagementLoading}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    <FormattedMessage id="close" />
                  </Button>
                  {isOperationPermittedRoleBase(
                    ModuleType.TreatmentManagement,
                    ActionType.Edit
                  ) && (
                    <LoaderButton
                      type="submit"
                      iconName="bi-check-lg"
                      isLoading={props.getTreatmentManagementLoading}
                      text={props.id > -1 ? "button_update" : "button_register"}
                    />
                  )}
                  {props.id > -1 &&
                    isOperationPermittedRoleBase(
                      ModuleType.StoreManagement,
                      ActionType.Delete
                    ) && (
                      <Button
                        variant="danger"
                        type="button"
                        onClick={handleDelete}
                        disabled={props.getTreatmentManagementLoading}
                      >
                        <i className="bi bi-trash3-fill me-1"></i>
                        <FormattedMessage id="button_delete" />
                      </Button>
                    )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default DeletePopupHOC(AddTreatmentManagementForm);
